import React from 'react'
import { Select, Space, Tooltip } from 'antd'
import { injectIntl, FormattedMessage } from 'react-intl'
import moment from 'moment'
import { groupBy, sortBy, uniqBy } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'

import {
	convertTimezoneIANAToUTCFormat,
	resolveDeviceTimezone,
	resolveTimezones,
	toTimezone,
	wallClockDateIn
} from '../utils/date'

// Schedules are floating wall clock times played in each device timezone,
// so the calendar current time indicator must read the wall clock of the
// device the user is looking at. These helpers back the device selector
// shared by the program and message program schedulers.

// value of the selector and of the "device" query param
// to display the current time in the browser timezone
export const LOCAL_TIMEZONE = 'local'

// the selectable devices with a known timezone, sorted by name
export const deviceTimezoneOptions = (devices) =>
	sortBy(
		uniqBy(devices.filter(Boolean), '_id')
			.map((d) => ({
				key: d._id,
				name: d.nickname || d.name || d._id,
				timezone: resolveDeviceTimezone(d.timeZone)
			}))
			.filter((o) => o.timezone),
		(o) => o.name.toLowerCase()
	)

// the device from the "device" query param, else the only device,
// else the local time: with several devices none is a better guess
export const initialTimezoneKey = (options, search) => {
	const requested = new URLSearchParams(search).get('device')
	if (
		requested === LOCAL_TIMEZONE ||
		options.some((o) => o.key === requested)
	) {
		return requested
	}
	return options.length === 1 ? options[0].key : LOCAL_TIMEZONE
}

// keep the selection on reload, without going through the router
// which would trigger the unsaved changes prompt
export const persistTimezoneKey = (timezoneKey) => {
	const url = new URL(window.location.href)
	url.searchParams.set('device', timezoneKey)
	window.history.replaceState(window.history.state, '', url.toString())
}

// The calendar only moves its indicator when getNow changes,
// so a new function is built for each selection.
export const nowGetter = (options, timezoneKey) => {
	const option = options.find((o) => o.key === timezoneKey)
	return option ? () => wallClockDateIn(option.timezone) : () => new Date()
}

const utcFormat = (timezone) => convertTimezoneIANAToUTCFormat(timezone)

const ScheduleTimezoneSelect = ({ options, value, onChange, intl }) => {
	const browserTimezone = resolveTimezones().browserTimezone
	const localLabel = intl.formatMessage({ id: 'scheduler.timezone.mine' })

	// one group per timezone, sorted by current UTC offset
	const groups = sortBy(
		Object.entries(groupBy(options, 'timezone')),
		([timezone]) => toTimezone(moment(), timezone).utcOffset(),
		([timezone]) => timezone
	)

	// the dropdown shows the device name under its timezone group,
	// the selection shows the `label` which carries the offset
	return (
		<Space size={4}>
			<span style={{ whiteSpace: 'nowrap' }}>
				<FormattedMessage id="scheduler.timezone.label" />
			</span>
			<Tooltip
				title={intl.formatMessage({ id: 'scheduler.timezone.info' })}
			>
				<FontAwesomeIcon icon={faInfoCircle} />
			</Tooltip>
			<Select
				data-test="schedule-timezone-select"
				popupClassName="multiline-select"
				style={{ width: 240 }}
				showSearch
				optionFilterProp="label"
				optionLabelProp="label"
				value={value}
				onChange={onChange}
			>
				{groups.map(([timezone, groupOptions]) => (
					<Select.OptGroup
						key={timezone}
						label={`${timezone} (${utcFormat(timezone)})`}
					>
						{groupOptions.map((o) => (
							<Select.Option
								key={o.key}
								value={o.key}
								label={`${o.name} (${utcFormat(timezone)})`}
							>
								{o.name}
							</Select.Option>
						))}
					</Select.OptGroup>
				))}
				<Select.OptGroup key={LOCAL_TIMEZONE} label={localLabel}>
					<Select.Option
						value={LOCAL_TIMEZONE}
						label={`${localLabel} (${utcFormat(browserTimezone)})`}
					>
						{`${browserTimezone} (${utcFormat(browserTimezone)})`}
					</Select.Option>
				</Select.OptGroup>
			</Select>
		</Space>
	)
}

export default injectIntl(ScheduleTimezoneSelect)
